const PERIOD_TYPE = {
	OVERTIME_ACTION:         'overtime_action',
	PENALTY_SHOOTOUT_ACTION: 'penalty_shootout_action',
	ACTION:                  'action',
	BREAK:                   'break',
};

const HALFTIME_START = 'halftime_start';
const HALFTIME_END = 'halftime_end';
const THIRD_START = 'third_start';
const THIRD_END = 'third_end';
const QUARTER_START = 'quarter_start';
const QUARTER_END = 'quarter_end';
const OVERTIME_START = 'overtime_start';
const OVERTIME_END = 'overtime_end';
const PENALTY_SHOOTOUT_START = 'penalty_shootout_start';
const PENALTY_SHOOTOUT_END = 'penalty_shootout_end';
const CLOCK_START = 'clock_start';
const CLOCK_STOP = 'clock_end';
const CLOCK_SET = 'clock_set';

const gamePhaseTagNames = new Set([HALFTIME_START, HALFTIME_END, THIRD_START, THIRD_END, QUARTER_START, QUARTER_END]);
const gamePhaseStartTagNames = new Set([HALFTIME_START, THIRD_START, QUARTER_START]);
const gamePhaseEndTagNames = new Set([HALFTIME_END, THIRD_END, QUARTER_END]);
const startTagNames = new Set([HALFTIME_START, THIRD_START, QUARTER_START, PENALTY_SHOOTOUT_START, OVERTIME_START]);
const penaltyShootoutTagNames = new Set([PENALTY_SHOOTOUT_START, PENALTY_SHOOTOUT_END]);
const overtimeTagNames = new Set([OVERTIME_START, OVERTIME_END]);
const clockTagNames = new Set([CLOCK_START, CLOCK_STOP, CLOCK_SET]);

const getPeriods = ({ event, tags }) => {
	const periodTags = tags.filter((item) =>
		gamePhaseTagNames.has(item.tag.name) ||
		overtimeTagNames.has(item.tag.name) ||
		penaltyShootoutTagNames.has(item.tag.name)
	);
	periodTags.sort((a, b) => a.timestamp - b.timestamp);

	const tagPeriods = [];

	if (periodTags.length > 0) {
		if (!gamePhaseStartTagNames.has(periodTags[0].tag.name)) {
			console.log('Warning: Expected the first period tag to be a starting tag. Unable to determine game phases.');
		} else {
			for (let index = 0; index < periodTags.length; index++) {
				if (index > 0 && periodTags[index].tag.name === periodTags[index - 1].tag.name) {
					console.log('Warning: Unexpected error, encountered consecutive period tags of the same type.');
				}

				if (startTagNames.has(periodTags[index].tag.name)) {
					if (index > 0) {
						tagPeriods.push({
							type:        PERIOD_TYPE.BREAK,
							start:       tagPeriods[tagPeriods.length - 1].end,
							end:         periodTags[index].timestamp,
							eventPeriod: event.periods?.length > tagPeriods.length ? event.periods[tagPeriods.length] : null,
						});
						if (tagPeriods.length > 1) tagPeriods[tagPeriods.length - 2].next = tagPeriods[tagPeriods.length - 1];
					}

					let type = PERIOD_TYPE.ACTION;
					if (periodTags[index].tag.name === OVERTIME_START) {
						type = PERIOD_TYPE.OVERTIME_ACTION;
					} else if (periodTags[index].tag.name === PENALTY_SHOOTOUT_START) {
						type = PERIOD_TYPE.PENALTY_SHOOTOUT_ACTION;
					}

					tagPeriods.push({
						type:        type,
						start:       periodTags[index].timestamp,
						end:         Number.POSITIVE_INFINITY,
						eventPeriod: event.periods?.length > tagPeriods.length ? event.periods[tagPeriods.length] : null,
					});
					if (tagPeriods.length > 1) tagPeriods[tagPeriods.length - 2].next = tagPeriods[tagPeriods.length - 1];
				}

				if (gamePhaseEndTagNames.has(periodTags[index].tag.name)) {
					tagPeriods[tagPeriods.length - 1].end = periodTags[index].timestamp;
				}
			}
		}
	}

	if (tagPeriods.length === 0) {
		tagPeriods.push({
			type:        PERIOD_TYPE.BREAK,
			start:       0,
			end:         Number.POSITIVE_INFINITY,
			eventPeriod: event.periods?.length > 0 ? event.periods[0] : null,
		});
	}

	return tagPeriods;
};

const getPeriod = ({ position, event, tags, periods = null, additionalTimeAfterPeriodEnd = 0 }) => {
	const gamePeriods = Array.isArray(periods) ? periods : getPeriods({ event, tags });

	for (const period of gamePeriods) {
		if (period.start <= position && (period.end + additionalTimeAfterPeriodEnd) >= position) {
			return {
				...period,
				hasEnded: period.end < position,
			};
		}
	}
	return null;
};

const getGameTime = ({ position, event, tags, periods = null }) => {
	const gamePeriods = Array.isArray(periods) ? periods : getPeriods({ event, tags });
	const currentPeriod = getPeriod({ position, event, tags, periods: gamePeriods });
	if (
		position < 0 ||
		!(
			currentPeriod?.type === 'action' ||
			(currentPeriod?.type === 'break' && currentPeriod?.start === position) ||
			(currentPeriod?.type === 'break' && currentPeriod?.end === position)
		)
	) {
		return { minutes: 0, seconds: 0, gameMinute: 0 };
	}

	const clockTags = tags.filter(
		(eventTag) =>
			clockTagNames.has(eventTag.tag.name) &&
			currentPeriod.start <= eventTag.timestamp &&
			eventTag.timestamp <= currentPeriod.end &&
			eventTag.timestamp < position
	);
	clockTags.sort((a, b) => a.timestamp - b.timestamp);

	let gameSeconds = 0;
	if (currentPeriod.type === 'action') {
		gameSeconds = (currentPeriod.eventPeriod?.startMinutes ?? 0) * 60;
	}
	if (currentPeriod.type === 'break' && Number.isFinite(currentPeriod.next?.eventPeriod?.startMinutes)) {
		gameSeconds = (currentPeriod.next?.eventPeriod?.startMinutes ?? 0) * 60;
	}

	if (currentPeriod.type === 'action') {
		let lastClockStartPosition = currentPeriod.start;
		for (const clockTag of clockTags) {
			switch (clockTag.tag.name) {
			case CLOCK_START: {
				if (clockTag.gameTimeSeconds) {
					gameSeconds = clockTag.gameTimeSeconds;
				}
				lastClockStartPosition = clockTag.timestamp;
				break;
			}
			case CLOCK_STOP: {
				if (Number.isFinite(lastClockStartPosition)) {
					if (clockTag.gameTimeSeconds) {
						gameSeconds = clockTag.gameTimeSeconds;
					} else {
						gameSeconds += clockTag.timestamp - lastClockStartPosition;
					}
				}
				lastClockStartPosition = null;
				break;
			}
			case CLOCK_SET: {
				gameSeconds = Number.parseInt(clockTag.value);
				if (lastClockStartPosition) lastClockStartPosition = clockTag.timestamp;
				break;
			}
			default: {
				throw new Error('Expected a clock tag.');
			}
			}
		}
		if (lastClockStartPosition) gameSeconds += position - lastClockStartPosition;
	}

	if (gameSeconds > 0) {
		return gameSecondsToGameTime(gameSeconds);
	}

	return {
		minutes:    null,
		seconds:    null,
		gameMinute: null,
	};
};

const gameSecondsToGameTime = (gameSeconds) => {
	const minutes = Math.floor(gameSeconds / 60);

	return {
		minutes,
		seconds:    gameSeconds % 60,
		gameMinute: minutes + 1,
	};
};

export { getGameTime, getPeriods, getPeriod, gameSecondsToGameTime, PERIOD_TYPE };
